import {
    AfterViewInit,
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { DataTableDirective } from 'angular-datatables';
import { environment } from 'src/environments/environment';
import { helper } from 'src/environments/helper';
import {
    AuditArchivesService,
    ArchiveFile,
    ArchiveStatus,
    ArchiveLogItem
} from 'src/app/services/audit-archives.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-audit-archives',
    templateUrl: './audit-archives.component.html',
    styleUrls: ['./audit-archives.component.scss'],
})
export class AuditArchivesComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild(DataTableDirective, { static: false })
    dtElement!: DataTableDirective;
    helper = helper;

    // UI State
    isLoading = false;
    isLoadingArchives = false;
    isLoadingStatus = false;
    isExporting = false;
    showFilters = false;
    activeTab: 'management' | 'viewer' = 'management';

    // Archive management
    availableArchives: ArchiveFile[] = [];
    archiveStatus: ArchiveStatus = { has_active_session: false, session: null };
    loadingArchivePath: string | null = null;

    // Filters for viewer
    dateFrom: string = '';
    dateTo: string = '';
    moduleFilter: string = '';
    actionFilter: string = '';
    userNameFilter: string = '';
    searchFilter: string = '';

    // Dropdown options
    modules: { value: string; label: string }[] = [];
    actions: { value: string; label: string }[] = [];

    // DataTable
    dtOptions: any = {};
    dtTrigger: Subject<any> = new Subject<any>();
    dtInitialized = false;

    constructor(private archivesService: AuditArchivesService) {}

    ngOnInit(): void {
        this.loadArchiveStatus();
        this.loadAvailableArchives();
        this.loadActions();
    }

    ngOnDestroy(): void {
        this.dtTrigger.unsubscribe();
    }

    ngAfterViewInit(): void {
        // DataTable will be initialized when viewer tab is active
    }

    // ==================== TAB MANAGEMENT ====================

    switchTab(tab: 'management' | 'viewer'): void {
        this.activeTab = tab;
        if (tab === 'viewer' && this.archiveStatus.has_active_session) {
            this.loadModulesFromArchive();
            if (!this.dtInitialized) {
                this.initDataTable();
            } else {
                this.rerenderDT();
            }
        }
    }

    // ==================== ARCHIVE MANAGEMENT ====================

    loadArchiveStatus(): void {
        this.isLoadingStatus = true;
        this.archivesService.getStatus().subscribe({
            next: (response) => {
                this.isLoadingStatus = false;
                if (response.success) {
                    this.archiveStatus = response.data;
                }
            },
            error: (err) => {
                this.isLoadingStatus = false;
                console.error('Error loading archive status:', err);
            }
        });
    }

    loadAvailableArchives(): void {
        this.isLoadingArchives = true;
        this.archivesService.getAvailableArchives().subscribe({
            next: (response) => {
                this.isLoadingArchives = false;
                if (response.success) {
                    this.availableArchives = response.data;
                }
            },
            error: (err) => {
                this.isLoadingArchives = false;
                console.error('Error loading available archives:', err);
            }
        });
    }

    loadArchive(archive: ArchiveFile): void {
        if (this.archiveStatus.has_active_session) {
            Swal.fire({
                title: 'Atenție',
                text: 'Există deja o arhivă încărcată. Descărcați-o înainte de a încărca alta.',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        Swal.fire({
            title: 'Confirmare încărcare',
            html: `
                <p>Doriți să încărcați arhiva <strong>${archive.period_label}</strong>?</p>
                <p class="text-muted">
                    ${archive.record_count ? archive.record_count.toLocaleString() + ' înregistrări' : 'Nr. înregistrări necunoscut'}<br>
                    ${archive.file_size_formatted}
                </p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Încarcă',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#28a745',
        }).then((result) => {
            if (result.isConfirmed) {
                this.performLoadArchive(archive);
            }
        });
    }

    performLoadArchive(archive: ArchiveFile): void {
        this.loadingArchivePath = archive.path;

        Swal.fire({
            title: 'Se încarcă arhiva...',
            html: `<p>${archive.period_label}</p><p class="text-muted">Vă rugăm așteptați...</p>`,
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        this.archivesService.loadArchive(archive.path).subscribe({
            next: (response) => {
                this.loadingArchivePath = null;
                Swal.close();

                if (response.success) {
                    Swal.fire({
                        title: 'Arhivă încărcată',
                        html: `
                            <p><strong>${response.period_label}</strong></p>
                            <p>${response.records_imported?.toLocaleString()} înregistrări importate</p>
                            <p class="text-muted">Expiră la: ${response.expires_at}</p>
                        `,
                        icon: 'success',
                        confirmButtonText: 'OK'
                    });
                    this.loadArchiveStatus();
                    this.loadModulesFromArchive();
                } else {
                    Swal.fire({
                        title: 'Eroare',
                        text: response.error || 'Eroare la încărcarea arhivei.',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                }
            },
            error: (err) => {
                this.loadingArchivePath = null;
                Swal.close();
                console.error('Error loading archive:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Eroare la încărcarea arhivei.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    unloadArchive(): void {
        if (!this.archiveStatus.has_active_session) {
            return;
        }

        Swal.fire({
            title: 'Confirmare descărcare',
            html: `
                <p>Doriți să descărcați arhiva <strong>${this.archiveStatus.session?.period_label}</strong>?</p>
                <p class="text-muted">Datele importate vor fi șterse din baza de date.</p>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Descarcă',
            cancelButtonText: 'Anulează',
            confirmButtonColor: '#dc3545',
        }).then((result) => {
            if (result.isConfirmed) {
                this.performUnloadArchive();
            }
        });
    }

    performUnloadArchive(): void {
        Swal.fire({
            title: 'Se descarcă arhiva...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        this.archivesService.unloadArchive().subscribe({
            next: (response) => {
                Swal.close();

                if (response.success) {
                    Swal.fire({
                        title: 'Arhivă descărcată',
                        text: `${response.records_deleted?.toLocaleString()} înregistrări șterse.`,
                        icon: 'success',
                        confirmButtonText: 'OK'
                    });
                    this.archiveStatus = { has_active_session: false, session: null };
                    this.activeTab = 'management';
                } else {
                    Swal.fire({
                        title: 'Eroare',
                        text: response.error || 'Eroare la descărcarea arhivei.',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                }
            },
            error: (err) => {
                Swal.close();
                console.error('Error unloading archive:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Eroare la descărcarea arhivei.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        });
    }

    // ==================== VIEWER ====================

    loadModulesFromArchive(): void {
        this.archivesService.getModules().subscribe({
            next: (response) => {
                if (response.success) {
                    this.modules = response.data;
                }
            },
            error: (err) => {
                console.error('Error loading modules:', err);
            }
        });
    }

    loadActions(): void {
        this.archivesService.getActions().subscribe({
            next: (response) => {
                if (response.success) {
                    this.actions = response.data;
                }
            },
            error: (err) => {
                console.error('Error loading actions:', err);
            }
        });
    }

    initDataTable(): void {
        const self = this;
        this.dtOptions = {
            serverSide: true,
            searching: false,
            responsive: true,
            pageLength: 25,
            order: [[0, 'desc']],

            ajax: (dataTablesParameters: any, callback: any) => {
                self.isLoading = true;

                const params: any = {
                    draw: dataTablesParameters.draw,
                    start: dataTablesParameters.start,
                    length: dataTablesParameters.length,
                };

                // Sorting
                if (dataTablesParameters.order && dataTablesParameters.order.length > 0) {
                    const orderCol = dataTablesParameters.order[0].column;
                    const orderDir = dataTablesParameters.order[0].dir;
                    const columns = ['original_id', 'user_name', 'module', 'action', 'model_name', 'result', 'original_created_at'];
                    params.sort_by = columns[orderCol] || 'original_id';
                    params.sort_dir = orderDir;
                }

                // Filters
                if (self.dateFrom) params.date_from = self.dateFrom;
                if (self.dateTo) params.date_to = self.dateTo;
                if (self.moduleFilter) params.module = self.moduleFilter;
                if (self.actionFilter) params.action = self.actionFilter;
                if (self.userNameFilter) params.user_name = self.userNameFilter;
                if (self.searchFilter) params.search = self.searchFilter;

                self.archivesService.getArchiveLogs(params).subscribe({
                    next: (resp: any) => {
                        self.isLoading = false;
                        callback({
                            recordsTotal: resp.recordsTotal || 0,
                            recordsFiltered: resp.recordsFiltered || 0,
                            data: resp.data || [],
                        });
                    },
                    error: (err) => {
                        self.isLoading = false;
                        console.error('Error fetching archive logs:', err);
                        callback({
                            recordsTotal: 0,
                            recordsFiltered: 0,
                            data: [],
                        });
                    }
                });
            },
            language: environment.config.romanianLanguageDataTable,
            columns: [
                {
                    title: 'ID Orig.',
                    data: 'original_id',
                    width: '70px',
                },
                {
                    title: 'Utilizator',
                    data: 'user_name',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'Modul',
                    data: 'module',
                    render: (data: string) => {
                        if (!data) return '-';
                        const parts = data.split('.');
                        return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' > ');
                    },
                },
                {
                    title: 'Acțiune',
                    data: 'action_label',
                    render: (data: string, type: any, row: any) => {
                        const actionColors: Record<string, string> = {
                            'create': 'success',
                            'update': 'primary',
                            'delete': 'danger',
                            'status_change': 'warning',
                            'view': 'info',
                            'list': 'secondary',
                            'export': 'dark',
                            'login': 'success',
                            'logout': 'secondary',
                        };
                        const color = actionColors[row.action] || 'secondary';
                        return `<span class="badge bg-${color}">${data || row.action || '-'}</span>`;
                    },
                },
                {
                    title: 'Obiect',
                    data: 'model_name',
                    render: (data: string, type: any, row: any) => {
                        if (!data && !row.short_model_type) return '-';
                        const modelType = row.short_model_type || '';
                        const modelName = data || '';
                        if (modelType && modelName) {
                            return `<small class="text-muted">${modelType}</small><br>${modelName}`;
                        }
                        return modelType || modelName || '-';
                    },
                },
                {
                    title: 'Rezultat',
                    data: 'result_label',
                    render: (data: string, type: any, row: any) => {
                        const resultColor = row.result === 'success' ? 'success' : 'danger';
                        return `<span class="badge bg-${resultColor}">${data || '-'}</span>`;
                    },
                },
                {
                    title: 'Data (Orig.)',
                    data: 'display_date',
                    render: (data: string) => data || '-',
                },
                {
                    title: 'Acțiuni',
                    data: null,
                    orderable: false,
                    className: 'all',
                    render: (row: any) => {
                        return `
                        <div class="d-flex align-center justify-content-left">
                            <button class="action-btn view ms-1" title="Vizualizează detalii" data-id="${row.id}">
                                <i class="fa-solid fa-eye"></i>
                            </button>
                        </div>
                        `;
                    },
                },
            ],
            rowCallback: (row: Node, data: ArchiveLogItem) => {
                const lastTd = (row as HTMLElement).querySelector('td:last-child');
                const viewBtn = lastTd?.querySelector('.view') as HTMLElement;

                if (viewBtn) {
                    viewBtn.addEventListener('click', () => {
                        this.viewDetails(data);
                    });
                }

                return row;
            },
        };

        this.dtInitialized = true;
        setTimeout(() => {
            this.dtTrigger.next('');
        }, 100);
    }

    rerenderDT(resetPagination: boolean = false): void {
        if (this.dtElement && this.dtElement.dtInstance) {
            this.dtElement.dtInstance.then((dtInstance: DataTables.Api) => {
                dtInstance.draw(resetPagination);
            });
        }
    }

    toggleFilters(): void {
        this.showFilters = !this.showFilters;
    }

    resetFilters(): void {
        this.dateFrom = '';
        this.dateTo = '';
        this.moduleFilter = '';
        this.actionFilter = '';
        this.userNameFilter = '';
        this.searchFilter = '';
        this.rerenderDT(true);
    }

    applyFilters(): void {
        this.rerenderDT(true);
    }

    viewDetails(data: ArchiveLogItem): void {
        this.archivesService.getArchiveLogDetail(data.id).subscribe({
            next: (response: any) => {
                const detail = response.data || response;

                const actionColors: Record<string, string> = {
                    'create': '#28a745',
                    'update': '#007bff',
                    'delete': '#dc3545',
                    'status_change': '#ffc107',
                    'view': '#17a2b8',
                    'list': '#6c757d',
                    'export': '#343a40',
                    'login': '#28a745',
                    'logout': '#6c757d',
                };
                const actionColor = actionColors[detail.action] || '#6c757d';

                const formatDiff = (diff: any) => {
                    if (!diff || Object.keys(diff).length === 0) return '';
                    let html = '<table class="table table-sm table-bordered" style="font-size: 12px; margin: 0;">';
                    html += '<thead><tr><th>Câmp</th><th>Valoare veche</th><th>Valoare nouă</th></tr></thead><tbody>';
                    for (const key of Object.keys(diff)) {
                        const old = diff[key].old !== null ? JSON.stringify(diff[key].old) : '-';
                        const newVal = diff[key].new !== null ? JSON.stringify(diff[key].new) : '-';
                        html += `<tr><td><strong>${key}</strong></td><td style="background:#ffebee;">${old}</td><td style="background:#e8f5e9;">${newVal}</td></tr>`;
                    }
                    html += '</tbody></table>';
                    return html;
                };

                const htmlContent = `
                    <div style="text-align: left; font-size: 14px; max-height: 70vh; overflow-y: auto;">
                        <table style="width: 100%; border-collapse: collapse;">
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; width: 140px;">ID Original:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.original_id || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Data originală:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.display_date || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Utilizator:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.user_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Direcție:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.directie_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Compartiment:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.compartiment_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Funcție:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.functie_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Modul:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.module || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Acțiune:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">
                                    <span style="background: ${actionColor}; color: white; padding: 3px 10px; border-radius: 4px; font-size: 12px;">
                                        ${detail.action_label || detail.action || '-'}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Model:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.short_model_type || '-'} (ID: ${detail.model_id || '-'})</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Nume model:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.model_name || '-'}</td>
                            </tr>
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">Rezultat:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">
                                    <span class="badge bg-${detail.result === 'success' ? 'success' : 'danger'}">${detail.result_label || detail.result || '-'}</span>
                                </td>
                            </tr>
                            ${detail.error_message ? `
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600; vertical-align: top;">Eroare:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; color: #dc3545;">${detail.error_message}</td>
                            </tr>
                            ` : ''}
                            <tr>
                                <td style="padding: 8px; border-bottom: 1px solid #eee; font-weight: 600;">IP:</td>
                                <td style="padding: 8px; border-bottom: 1px solid #eee;">${detail.ip_address || '-'}</td>
                            </tr>
                            ${detail.diff && Object.keys(detail.diff).length > 0 ? `
                            <tr>
                                <td colspan="2" style="padding: 8px; border-bottom: 1px solid #eee;">
                                    <strong>Modificări:</strong>
                                    <div style="margin-top: 8px;">${formatDiff(detail.diff)}</div>
                                </td>
                            </tr>
                            ` : ''}
                        </table>
                    </div>
                `;

                Swal.fire({
                    title: '<i class="fa-solid fa-clipboard-list"></i> Detalii Log Arhivă',
                    html: htmlContent,
                    width: 700,
                    confirmButtonText: 'Închide',
                    confirmButtonColor: '#3085d6',
                });
            },
            error: (err: any) => {
                console.error('Error fetching archive log detail:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Nu s-au putut încărca detaliile.',
                    icon: 'error',
                    confirmButtonText: 'Închide',
                });
            }
        });
    }

    exportCsv(): void {
        this.isExporting = true;
        const params: any = {};
        if (this.dateFrom) params.date_from = this.dateFrom;
        if (this.dateTo) params.date_to = this.dateTo;
        if (this.moduleFilter) params.module = this.moduleFilter;
        if (this.actionFilter) params.action = this.actionFilter;
        if (this.userNameFilter) params.user_name = this.userNameFilter;
        if (this.searchFilter) params.search = this.searchFilter;

        this.archivesService.exportCsv(params).subscribe({
            next: (blob) => {
                this.isExporting = false;
                const periodLabel = this.archiveStatus.session?.period_label?.replace(' ', '_') || 'archive';
                this.archivesService.downloadFile(blob, `audit-archive-${periodLabel}-${new Date().toISOString().split('T')[0]}.csv`);
            },
            error: (err) => {
                this.isExporting = false;
                console.error('Error exporting CSV:', err);
                Swal.fire({
                    title: 'Eroare',
                    text: 'Eroare la export CSV.',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        });
    }
}
